import { Outlet, useLocation, Link } from 'react-router-dom';
import { AppSidebar } from './AppSidebar';
import { UserAvatarMenu } from './UserAvatarMenu';
import { LayoutDashboard } from 'lucide-react';
import { usePagePerformance } from '@/hooks/usePagePerformance';
import { SidebarProvider, SidebarTrigger, SidebarInset } from '@/components/ui/sidebar';

// Route configuration for breadcrumb generation
const routeConfig: Array<{
  pattern: RegExp;
  segments: Array<{ label: string; path?: string }>;
}> = [
  { pattern: /^\/dashboard$/, segments: [{ label: 'Dashboard' }] },
  { pattern: /^\/vendors$/, segments: [{ label: 'Vendors' }] },
  { pattern: /^\/vendors\/[^/]+$/, segments: [
    { label: 'Vendors', path: '/vendors' },
    { label: 'Vendor' }
  ]},
  { pattern: /^\/price-books$/, segments: [{ label: 'Price Books' }] },
  { pattern: /^\/quotes$/, segments: [{ label: 'Supplier Quotes' }] },
  { pattern: /^\/quotes\/upload$/, segments: [
    { label: 'Supplier Quotes', path: '/quotes' },
    { label: 'Upload Quote' }
  ]},
  { pattern: /^\/price-books\/catalog$/, segments: [{ label: 'Catalog' }] },
  { pattern: /^\/price-books\/[^/]+$/, segments: [
    { label: 'Price Books', path: '/price-books' },
    { label: 'Price Book' }
  ]},
  { pattern: /^\/invoices$/, segments: [{ label: 'Invoices' }] },
  { pattern: /^\/invoices\/[^/]+$/, segments: [
    { label: 'Invoices', path: '/invoices' },
    { label: 'Invoice' }
  ]},
  { pattern: /^\/invoices\/[^/]+\/quote-basis$/, segments: [
    { label: 'Invoices', path: '/invoices' },
    { label: 'Invoice (Quote Basis)' }
  ]},
  { pattern: /^\/field-tickets$/, segments: [{ label: 'Field Tickets' }] },
  { pattern: /^\/field-tickets\/[^/]+$/, segments: [
    { label: 'Field Tickets', path: '/field-tickets' },
    { label: 'Field Ticket' }
  ]},
  { pattern: /^\/company-profile$/, segments: [{ label: 'Company Profile' }] },
  { pattern: /^\/my-profile$/, segments: [{ label: 'My Profile' }] },
  { pattern: /^\/privacy$/, segments: [{ label: 'Privacy & Compliance' }] },
  { pattern: /^\/security\/users$/, segments: [
    { label: 'Security', path: '/security/users' },
    { label: 'Users' }
  ]},
  { pattern: /^\/security\/groups$/, segments: [
    { label: 'Security', path: '/security/users' },
    { label: 'Groups' }
  ]},
  { pattern: /^\/security\/permissions$/, segments: [
    { label: 'Security', path: '/security/users' },
    { label: 'Permissions' }
  ]},
  { pattern: /^\/configuration$/, segments: [{ label: 'Configuration' }] },
  { pattern: /^\/configuration\/external-systems\/[^/]+$/, segments: [
    { label: 'Configuration', path: '/configuration' },
    { label: 'External System' }
  ]},
  { pattern: /^\/usage$/, segments: [{ label: 'Usage' }] },
  { pattern: /^\/tools\/extract-invoice$/, segments: [{ label: 'Extract Invoice' }] },
  { pattern: /^\/tools\/validate-invoice$/, segments: [{ label: 'Validate Invoice' }] },
  { pattern: /^\/tools\/early-pay$/, segments: [{ label: 'Early Pay' }] },
  { pattern: /^\/pdf$/, segments: [{ label: 'PDF Viewer' }] },
];

function getBreadcrumbSegments(pathname: string): Array<{ label: string; path?: string }> {
  for (const route of routeConfig) {
    if (route.pattern.test(pathname)) {
      return route.segments;
    }
  }
  return [{ label: 'Dashboard' }];
}

export function AppLayout() {
  const location = useLocation();
  const segments = getBreadcrumbSegments(location.pathname);

  // Track page performance for all protected routes
  usePagePerformance();

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        {/* Top Header Bar */}
        <header className="h-14 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-6 sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="mr-2" />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <LayoutDashboard className="w-4 h-4" />
              {segments.map((segment, index) => (
                <span key={index} className="flex items-center gap-2">
                  <span className="text-muted-foreground">/</span>
                  {segment.path ? (
                    <Link 
                      to={segment.path} 
                      className="hover:text-foreground transition-colors"
                    >
                      {segment.label}
                    </Link>
                  ) : (
                    <span className="text-foreground font-medium">{segment.label}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
          <UserAvatarMenu />
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto animate-fade-in">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
